// src/components/About.js
import React, { useRef, useEffect, useState } from 'react';
import {
    motion,
    useScroll,
    useTransform,
    useInView,
    useMotionValue,
    animate,
} from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import mainLogo         from '../assets/images/new/logoCC.png';


const content = {
    en: {
        headerTitle: 'About Us',
    },
    ar: {
        headerTitle: 'من نحن',
    },
};
const About = () => {
    const { language } = useLanguage();
    const t = content[language] || content.en;
    // Section scroll tracking for parallax effect
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ['start end', 'end start'],
    });
    const y = useTransform(scrollYProgress, [0, 1], [0, -50]);
    const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.2]);

    // Main content in-view tracking (for text)
    const contentRef = useRef(null);
    const inView = useInView(contentRef, {
        once: false,
        amount: 0.1,
        rootMargin: '0px 0px -100px 0px',
    });

    // Logo in-view tracking
    const logoRef = useRef(null);
    const logoVisible = useInView(logoRef, { once: false, amount: 0.5 });

    const statsRef = useRef(null);
    const statsInView = useInView(statsRef, { once: false, amount: 0.4 });
    const mvProjects = useMotionValue(0);
    const mvCountries = useMotionValue(0);
    const mvSatisfaction = useMotionValue(0);
    const [projectsCount, setProjectsCount] = useState(0);
    const [countriesCount, setCountriesCount] = useState(0);
    const [satisfactionCount, setSatisfactionCount] = useState(0);

    useEffect(() => {
        if (!statsInView) return;
        const unsubProjects = mvProjects.on("change", (v) => setProjectsCount(Math.round(v)));
        const unsubCountries = mvCountries.on("change", (v) => setCountriesCount(Math.round(v)));
        const unsubSatisfaction = mvSatisfaction.on("change", (v) => setSatisfactionCount(Math.round(v)));
        return () => { unsubProjects(); unsubCountries(); unsubSatisfaction(); };
    }, [statsInView, mvProjects, mvCountries, mvSatisfaction]);

    useEffect(() => {
        if (statsInView) {
            animate(mvProjects, 500, { duration: 1.5, ease: "easeOut" });
            animate(mvCountries, 4, { duration: 1.5, ease: "easeOut" });
            animate(mvSatisfaction, 99, { duration: 1.5, ease: "easeOut" });
        } else {
            mvProjects.set(0); mvCountries.set(0); mvSatisfaction.set(0);
        }
    }, [statsInView, mvProjects, mvCountries, mvSatisfaction]);


    // Variants
    const bentoVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: i => ({
            opacity: 1,
            y: 0,
            transition: { delay: i * 0.15, duration: 0.6, ease: [0.16, 1, 0.3, 1] },
        }),
    };

    const logoVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
    };

    const heading = {
        en: 'Empowering brands with innovative strategies since 2018.',
        ar: 'من نحن',
    };
    const paragraphs = {
        en: ['Creativad is a leading business and marketing consultancy company.', 'Specialized in digital marketing, branding, web design, social media management and media creation.', 'With 500+ successful projects across 15+ countries worldwide.', 'Pioneering AI-powered marketing solutions since 2022.',],
        ar: ['كريتيفاد, شركة استشارية رائدة منذ عام ٢٠١٨ في الادارة والتسويق, تقدم حلولاً مبتكرة لدعم نمو العلامات التجارية.', 'نحن متخصصون في التسويق الرقمي، وبناء الهوية والعلامة التجارية، وتصميم المواقع الإلكترونية، وإدارة وسائل التواصل الاجتماعي، وإنشاء المحتوى، بهدف مساعدة الشركات على تعزيز حضورها وتحقيق أهدافها الاستراتيجية.', 'بفضل خبرتنا الواسعة، أنجزنا أكثر من ٥٠٠ مشروعًا ناجحًا في مختلف الأسواق المحلية والعالمية.', 'منذ عام ٢٠٢٢، كانت كريتيفاد في طليعة تبني تقنيات الذكاء الاصطناعي في التسويق، مما أحدث تحولًا نوعيًا في تطوير الاستراتيجيات ودفع عجلة نمو الأعمال.',],
    };
    const stats = [
        { en: '500+', ar: '٥٠٠+', label: { en: 'Projects', ar: 'المشاريع' } },
        { en: '4', ar: '٤', label: { en: 'Countries', ar: 'الدول' } },
        { en: '99%', ar: '٩٩٪', label: { en: 'Happy Clients ', ar: 'عملاء سعداء' } },
        { en: 'AI', ar: 'الذكاء الاصطناعي', label: { en: 'Powered', ar: 'مدعوم' } },
    ];


    return (
        <section
            id="about"
            // Reduced vertical padding on mobile (py-12 = 3rem) vs desktop (pt-18/pb-10 = 7rem total).
            // Original: "relative pt-18 pb-10 bg-light overflow-hidden md:pl-48"
            className="relative py-1 bg-light overflow-hidden md:pl-48 md:pt-18 md:pb-10"
            ref={containerRef}
        >
            <div className="absolute inset-0 z-0 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl" />
            <motion.div
                className="absolute top-1/4 -right-40 w-[500px] h-[500px] rounded-full bg-gradient-to-r from-[#3D8361]/20 to-[#50A795]/20 blur-[100px]"
                style={{ y, opacity }}
            />
            <div className="container mx-auto px-4 relative z-10">
                <motion.h2
                    // Reduced margin on mobile
                    className="text-4xl md:text-5xl font-greycliff font-bold mb-8 md:mb-10 text-center"
                    initial={{ scale: 0.9 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300 }}>
                    {t.headerTitle}
                </motion.h2>
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-center">
                    <motion.div ref={logoRef} className="relative" style={{ y }} variants={logoVariants} initial="hidden" animate={logoVisible ? 'visible' : 'hidden'}>
                        {/* Smaller logo size on mobile */}
                        <div className="relative aspect-square max-w-[220px] md:max-w-[400px] mx-auto rounded-2xl overflow-hidden bg-white/10 backdrop-blur-sm border border-white/20 shadow-lg">
                            <div className="relative z-10 h-full flex items-center justify-center p-8">
                                <img src={mainLogo} alt="logo" className="w-full h-full object-contain" />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent" />
                        </div>
                    </motion.div>

                    <motion.div ref={contentRef} className="max-w-2xl mx-auto lg:mx-0">
                        <motion.h2
                            // Smaller font size and margin on mobile
                            className="text-3xl md:text-4xl font-greycliff font-bold mb-6 text-[#28826F] text-center lg:text-left"
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } : {}}
                        >
                            {language === 'en' ? heading.en : heading.ar}
                        </motion.h2>

                        <div className="space-y-4 mb-8 md:space-y-5 md:mb-12">
                            {paragraphs[language].map((text, i) => (
                                <motion.p key={i}
                                          className={`text-lg text-gray-700 ${language === 'ar' ? 'text-right dir-rtl' : ''} bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-[#50A795]/30`}
                                          custom={i} variants={bentoVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} >
                                    {language === 'en' && <span className="inline-block w-2 h-2 rounded-full bg-[#28826F] mr-3" />}
                                    {text}
                                </motion.p>
                            ))}
                        </div>

                        <motion.div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-4" initial="hidden" animate={statsInView ? 'visible' : 'hidden'} >
                            {stats.map((stat, i) => (
                                <motion.div key={i}
                                            className={`bg-white/80 backdrop-blur-sm border border-[#50A795]/30 rounded-2xl p-5 text-center shadow-[0_10px_40px_-15px_rgba(0,0,0,0.05)] transition-all hover:shadow-lg ${i === 3 ? 'bg-gradient-to-br from-[#3D8361]/10 to-[#50A795]/10' : ''}`}
                                            custom={i} variants={bentoVariants} >
                                    <div className="text-2xl font-greycliff font-bold text-[#28826F] mb-1">
                                        {i < 3 ? language === 'en' ? [projectsCount, countriesCount, `${satisfactionCount}%`][i] : stat.ar : stat.en}
                                    </div>
                                    <div className="text-xs font-greycliff font-normal text-gray-500 tracking-wider">
                                        {language === 'en' ? stat.label.en : stat.label.ar}
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default About;